import React, { useState } from "react";
import {
  Box, VStack, HStack, Heading, Text, FormControl,
  FormLabel, Input, Textarea, Select, Button,
  Divider, Icon, useColorModeValue, useToast,
  Alert, AlertIcon,
} from "@chakra-ui/react";
import { FaPhone, FaEnvelope, FaHeadset, FaClock } from "react-icons/fa";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import PublicLayout from './PublicLayout';
import bandpic from "../resources/EDUVIC B2C Hero3-coaching.png";

const FREE_TRIAL = "Free Trial Class";

const CATEGORIES = [
  FREE_TRIAL,
  "General Enquiry",
  "Pricing & Plans",
  "Content & Curriculum",
  "Partnerships",
  "Technical Question",
  "Other",
];

const ContactPage = () => {
  // The flyer QR code opens this page with ?topic=free-trial
  const [searchParams] = useSearchParams();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState(
    searchParams.get("topic") === "free-trial" ? FREE_TRIAL : ""
  );
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — real users never fill this
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;
  const supportPhone = process.env.REACT_APP_CONTACTUS_PHONE;

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const infoBg = useColorModeValue("purple.50", "purple.900");
  const infoBorderColor = useColorModeValue("purple.100", "purple.700");

  const isFreeTrial = category === FREE_TRIAL;

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async () => {
    if (!fullName.trim()) {
      toast({ title: "Please enter your name", status: "warning", duration: 3000 });
      return;
    }
    if (!email.trim() || !isValidEmail(email.trim())) {
      toast({ title: "Please enter a valid email address", status: "warning", duration: 3000 });
      return;
    }
    if (!category) {
      toast({ title: "Please select a category", status: "warning", duration: 3000 });
      return;
    }
    if (isFreeTrial && phone.replace(/\D/g, "").length < 8) {
      toast({
        title: "Please enter a contact number so we can confirm your trial class",
        status: "warning",
        duration: 4000,
      });
      return;
    }
    if (!message.trim() || message.trim().length < 20) {
      toast({ title: "Please describe your query in at least 20 characters", status: "warning", duration: 3000 });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${apiUrl}/b2c/public/contact-us`, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        category,
        message: message.trim(),
        website, // honeypot field, should always be empty
      });
      setSubmitted(true);
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Failed to submit. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
         <PublicLayout>
      <Box maxW="680px" mx="auto" p={6}>
        <Alert status="success" borderRadius="xl" p={6} flexDirection="column"
          alignItems="center" textAlign="center" gap={3}>
          <AlertIcon boxSize={8} />
          <Text fontWeight="bold" fontSize="lg">Message Sent</Text>
          <Text fontSize="sm" color={mutedText}>
            Thanks for reaching out. We've sent a confirmation to{" "}
            <strong>{email}</strong> and will get back to you as soon as possible.
          </Text>
          <Button mt={2} colorScheme="purple" size="sm"
            onClick={() => {
              setSubmitted(false);
              setFullName("");
              setEmail("");
              setPhone("");
              setCategory("");
              setMessage("");
            }}>
            Send Another Message
          </Button>
        </Alert>
      </Box>
      </PublicLayout>
    );
  }

  return (
        <PublicLayout>
            <Box>

      {/* Contact Hero Section */}
      <Box
        as="section"
        minH={{ base: "260px", md: "320px" }}
        bgImage={bandpic}
        bgSize="cover"
        bgPosition="right center"
        bgRepeat="no-repeat"
        position="relative"
        overflow="hidden"
      >
        {/* LEFT Gradient Overlay for readability */}
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          bgGradient="
            linear(
              to-r,
              rgba(0,0,0,0.68),
              rgba(0,0,0,0.35),
              rgba(0,0,0,0)
            )
          "
          zIndex={1}
        />

        {/* Content */}
        <Box
          position="relative"
          zIndex={2}
          minH={{ base: "260px", md: "320px" }}
          display="flex"
          alignItems="center"
          px={{ base: 6, md: 16 }}
        >
          <VStack
            align="flex-start"
            spacing={4}
            maxW="700px"
          >

            {/* Headline */}
            <Heading
              color="white"
              size="2xl"
              lineHeight="1.2"
            >
              Contact Us
            </Heading>

            {/* Subtext */}
            <Text
              color="gray.100"
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.7"
              maxW="650px"
            >
              Have a question about Eduvic before you get started?
              We'd love to help.
            </Text>

          </VStack>
        </Box>
      </Box>

    <Box maxW="1200px" mx="auto" p={6}>
{/*       <VStack align="start" spacing={1} mb={8}>
        <Heading size="lg">Contact Us</Heading>
        <Text color={mutedText}>
          Have a question about Eduvic before you get started? We'd love to help.
        </Text>
      </VStack> */}

      {/* Contact info strip */}
      <Box bg={infoBg} borderRadius="xl" p={5} mb={6}
        borderWidth="1px" borderColor={infoBorderColor}>
        <HStack spacing={8} align="stretch">
            {supportPhone && (
            <HStack spacing={3} flex={1}>
                <Icon as={FaPhone} color="purple.500" />
                <Box>
                <Text fontSize="xs" color={mutedText}>Call us</Text>
                <Text fontWeight="semibold" fontSize="sm">
                    {supportPhone}
                </Text>
                </Box>
            </HStack>
            )}

            <HStack spacing={3} flex={1}>
            <Icon as={FaEnvelope} color="purple.500" />
            <Box>
                <Text fontSize="xs" color={mutedText}>Email us</Text>
                <Text fontWeight="semibold" fontSize="sm">
                {process.env.REACT_APP_ENQUIRY_EMAIL ?? "enquiry@eduvic.com.au"}
                </Text>
            </Box>
            </HStack>
{/*           <HStack spacing={3}>
            <Icon as={FaClock} color="purple.500" />
            <Box>
              <Text fontSize="xs" color={mutedText}>Response time</Text>
              <Text fontWeight="semibold" fontSize="sm">Within 1 business day</Text>
            </Box>
          </HStack> */}
        </HStack>
      </Box>

      {/* Contact form */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px"
        borderColor={borderColor} p={6}>
        <HStack mb={5} spacing={2}>
          <Icon as={FaHeadset} color="purple.500" />
          <Text fontWeight="bold" fontSize="lg">Send Us a Message</Text>
        </HStack>

        <Divider mb={5} />

        <VStack spacing={5} align="stretch">

          <HStack spacing={4} align="start">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Full name</FormLabel>
              <Input
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Jane Smith"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="jane@example.com"
              />
            </FormControl>
          </HStack>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Category</FormLabel>
            <Select
              placeholder="Select the category that best fits your query"
              value={category}
              onChange={e => setCategory(e.target.value)}
              size="md"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
            {isFreeTrial && (
              <Text fontSize="xs" color={mutedText} mt={1}>
                We'll call or message you to confirm a free trial class time that suits you.
              </Text>
            )}
          </FormControl>

          <FormControl isRequired={isFreeTrial}>
            <FormLabel fontSize="sm">
              {isFreeTrial ? "Phone" : "Phone (optional)"}
            </FormLabel>
            <Input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={
                isFreeTrial
                  ? "So we can confirm your trial class"
                  : "Optional — if you'd like us to call you back"
              }
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Your message</FormLabel>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={
                isFreeTrial
                  ? "Please mention your child's year level, the exam you're preparing for, and your preferred days and times for a trial class."
                  : "Tell us a bit about what you're looking for or what you'd like to know."
              }
              rows={6}
              resize="vertical"
            />
            <Text fontSize="xs" color={mutedText} mt={1}>
              {message.length} characters — minimum 20 required
            </Text>
          </FormControl>

          {/* Honeypot field — hidden from real users via CSS, bots tend to fill every field */}
          <Box position="absolute" left="-9999px" aria-hidden="true">
            <FormLabel htmlFor="website">Website</FormLabel>
            <Input
              id="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={e => setWebsite(e.target.value)}
            />
          </Box>

          <Button
            colorScheme="purple"
            size="md"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Sending..."
            alignSelf="flex-start"
            px={8}
          >
            Send Message
          </Button>
        </VStack>
      </Box>
    </Box>
    </Box>
    </PublicLayout>
  );
};

export default ContactPage;